import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Sparkles, Loader2 } from "lucide-react";

/**
 * ProfessorRegenTile — small admin dashboard tile showing live
 * throughput of the `wua-regen-interpret` daemon so admins can eyeball
 * whether the Professor is keeping up with the queue.
 *
 * Data source: GET /api/admin/interpret/regen-status (self-refreshes 60s).
 */
export default function ProfessorRegenTile() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const r = await api.get("/admin/interpret/regen-status");
        if (alive) { setData(r.data); setErr(null); }
      } catch (e) {
        if (alive) setErr(e?.response?.data?.detail || "Failed to load");
      }
    };
    load();
    const t = setInterval(load, 60_000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  const eta = (min) => {
    if (min == null) return "—";
    if (min < 60) return `~${min}m`;
    const h = Math.floor(min / 60);
    if (h < 48) return `~${h}h`;
    return `~${Math.floor(h / 24)}d`;
  };

  return (
    <div
      data-testid="admin-regen-tile"
      className="p-5 border border-stone-300 rounded-xl bg-white"
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <Sparkles className="h-5 w-5 text-stone-800" />
          <h3 className="mt-2 font-editorial text-lg">Professor regen</h3>
          <p className="text-[10px] font-underground-sc tracking-wider text-stone-500 mt-1">
            Backfilling AI drafts for degrees with entry_requirements
          </p>
        </div>
      </div>

      {err && <div className="mt-3 text-xs text-rose-700">{err}</div>}
      {!data && !err && (
        <div className="mt-3 text-xs text-stone-500 flex items-center gap-2">
          <Loader2 className="h-3 w-3 animate-spin" /> Loading…
        </div>
      )}
      {data && (
        <>
          <div className="mt-3 grid grid-cols-2 gap-3">
            <Stat label="Remaining" value={data.remaining.toLocaleString()} big />
            <Stat label="ETA" value={eta(data.eta_minutes)} accent />
            <Stat label="Last hour" value={`+${data.written_last_hour}`} />
            <Stat label="Last 15m" value={`+${data.written_last_15m}`} />
          </div>
          <div className="mt-3 pt-3 border-t border-stone-100 text-[10px] font-mono text-stone-500 space-y-0.5">
            <div>Total written by daemon: {data.total_daemon_writes.toLocaleString()}</div>
            {data.latest_write && (
              <div className="truncate" title={data.latest_write.id}>
                Latest · {data.latest_write.title || data.latest_write.id}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ label, value, big, accent }) {
  return (
    <div>
      <div className="text-[9px] font-underground-sc tracking-wider text-stone-400">
        {label}
      </div>
      <div className={`mt-0.5 font-mono ${big ? "text-2xl font-semibold" : "text-lg"} ${accent ? "text-emerald-700" : "text-stone-800"}`}>
        {value}
      </div>
    </div>
  );
}
